/**
 * File Name          : screenservice.service.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 *
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ScreenserviceService {
  // matchMedia is retained only so its 'change' event can act as one more
  // signal in the listener chain below - the actual isLandscape value is
  // computed from window.innerWidth/innerHeight (see computeIsLandscape),
  // which stays accurate on Android Chrome even in states where
  // matchMedia lags (e.g. right after a Bluetooth permission dialog
  // dismisses - the RWAI-19701 repro).
  private orientationQuery = window.matchMedia('(orientation: landscape)');
  private isLandscape$ = new BehaviorSubject<boolean>(
    window.innerWidth > window.innerHeight,
  );

  width = window.innerWidth;
  height = window.innerHeight;

  constructor(private zone: NgZone) {
    this.updateDimensions();

    // The initial isLandscape$ value is captured at field-initializer time,
    // which can be read before the viewport/address-bar has settled on page
    // load - and since orientationchange/matchMedia only fire on an actual
    // orientation *change*, a stale initial read is never corrected if the
    // the same settle-and-recheck used for real orientation changes here too.
    this.handleOrientationSignal();

    window.addEventListener('resize', () =>
      this.zone.run(() => {
        this.updateDimensions();
        // Rotation on some devices fires resize but not orientationchange
        // (or fires resize first, while orientationchange lags behind), so
        // recompute here too - otherwise subscribers keep seeing the stale
        // pre-rotation value until a later event happens to fire.
        this.isLandscape$.next(this.computeIsLandscape());
      }),
    );

    this.orientationQuery.addEventListener('change', () =>
      this.zone.run(() => this.handleOrientationSignal()),
    );

    // Screen Orientation API as a second signal - fires independently of
    // matchMedia/resize timing on some devices, catching cases the other
    // listeners miss.
    const screenOrientation = (screen as any).orientation as
      | ScreenOrientation
      | undefined;
    screenOrientation?.addEventListener('change', () =>
      this.zone.run(() => this.handleOrientationSignal()),
    );

    window.addEventListener('orientationchange', () =>
      this.zone.run(() => this.handleOrientationSignal()),
    );
  }

  private handleOrientationSignal(): void {
    this.updateDimensions();
    this.isLandscape$.next(this.computeIsLandscape());

    // Re-check once layout has settled. Devices vary in how long the address
    // bar animation / viewport resize takes after rotation, so a single
    // immediate read isn't reliable across devices - this catches the cases
    // where the first read above was still stale.
    setTimeout(
      () =>
        this.zone.run(() => {
          this.updateDimensions();
          this.isLandscape$.next(this.computeIsLandscape());
        }),
      300,
    );
  }

  // Source of truth for "does the viewport currently have room for the
  // landscape chart layout". matchMedia's (orientation: landscape) has been
  // observed to lag or lock stale on some Android Chrome builds -
  // particularly after a Bluetooth permission dialog dismisses, which is
  // exactly the sequence the RWAI-19701 repro triggers - while
  // innerWidth/innerHeight track the actual laid-out viewport. Comparing
  // dimensions directly is what determines whether the chart fits, and
  // stays accurate even when matchMedia doesn't.
  private computeIsLandscape(): boolean {
    return window.innerWidth > window.innerHeight;
  }

  private updateDimensions(): void {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
  }

  // The resize/orientationchange/matchMedia listeners above only fire on an
  // actual device rotation - a modal that reads isLandscape while opening
  // (e.g. the mobile graph popup) never gets the settle-and-recheck safety
  // net if the device was already in its current orientation before the
  // modal opened. Call this right before showing such content so the same
  // settle-and-recheck runs on demand instead of only after a real rotation.
  recheckOrientation(): void {
    this.zone.run(() => this.handleOrientationSignal());
  }

  get isMobile() {
    // Read live dimensions rather than the cached width/height fields - those
    // are only refreshed by the resize/orientationchange listeners below, so
    // isMobile could disagree with the live isLandscape read (e.g. navigating
    // straight into a page before any resize/orientationchange fires), letting
    // the mobile and desktop chart branches render inconsistently together.
    return this.width < 768 || this.height < 768; // adjust breakpoint as needed
  }

  get isLandscape() {
    // Read live viewport dimensions rather than cached state or
    // matchMedia - matchMedia's (orientation: landscape) has been observed
    // to lag/lock stale on Android Chrome after a Bluetooth permission
    // dialog dismisses (RWAI-19701), showing the "Device must be in
    // Landscape" fallback while the device was clearly landscape.
    // Dimensions are what determine whether the chart fits, and are
    // always fresh.
    return window.innerWidth > window.innerHeight;
  }

  // Emits whenever the confirmed orientation state changes, including the
  // settled re-check - use this instead of a raw window resize/orientationchange
  // listener to trigger chart re-renders, since those raw events are exactly
  // what was firing before isLandscape had actually settled.
  get orientationChanges() {
    return this.isLandscape$.asObservable();
  }
}
